import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import router from './router';
import { useAppConfigStore, useAuthStore } from './store';
import { authApi } from '@/data/api';
import apiClient from '@/data/apiClient';
import { useSchoolBranding } from '@/hooks/useSchoolBranding';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster as HotToaster } from 'react-hot-toast';
import './index.css';
import { initPWA } from './utils/pwa.utils';

// ============================================================================
// SSO HANDOFF — the public website hands off a one-time token pair via URL
// fragment (never sent to any server as part of the URL). Tokens now live in
// httpOnly cookies, which JS can't set directly, so instead of stashing them
// in localStorage (the old approach) we hand them to the API's
// /auth/sso-exchange endpoint, which verifies them and sets cookies itself.
// Captured here (before React renders) so the hash never lingers in the URL
// or browser history; consumed once by the bootstrap effect below.
// ============================================================================

let pendingSsoRefreshToken: string | null = null;

(function captureSsoHandoffFromHash() {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash;
  if (!hash || !hash.includes('refresh=')) return;

  try {
    const params = new URLSearchParams(hash.slice(1));
    const refresh = params.get('refresh');
    const branchId = params.get('branchId');
    const academicYearId = params.get('academicYearId');

    if (refresh) pendingSsoRefreshToken = refresh;
    if (branchId) localStorage.setItem('branchId', branchId);
    if (academicYearId) localStorage.setItem('academicYearId', academicYearId);

    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch (err) {
     
    console.warn('[SSO] Failed to capture handoff from hash', err);
  }
})();

// ============================================================================
// AUTH BOOTSTRAP — ask the server (via the httpOnly session cookie) whether
// a session actually exists before route guards decide anything. This is
// the gate that prevents the login/dashboard flicker: without it, a guard
// has to guess from stale/absent local state on every fresh page load.
// ============================================================================

async function bootstrapAuth(): Promise<void> {
  let sessionFound = false;
  try {
    if (pendingSsoRefreshToken) {
      await apiClient.post('/auth/sso-exchange', { refreshToken: pendingSsoRefreshToken });
      pendingSsoRefreshToken = null;
    }

    const res = await authApi.getCurrentUser();
    if (res.success && res.data) {
      useAuthStore.getState().login({
        id: res.data.id,
        email: res.data.email,
        firstName: res.data.firstName,
        lastName: res.data.lastName,
        role: res.data.primaryRole,
        // Pass the FULL roles array from GET /auth/me too — otherwise this
        // bootstrap path (which runs on every full page load) overwrote the
        // roles with just `primaryRole`, dropping secondary roles for
        // multi-role accounts (e.g. teacher+student) and mis-resolving the
        // role-based dashboard / route guards.
        roles: res.data.roles,
        permissions: res.data.permissions,
        avatar: res.data.avatar,
        branchId: res.data.branchId,
        academicYearId: res.data.academicYearId,
        studentId: res.data.studentId,
        employeeId: res.data.employeeId,
        parentId: res.data.parentId,
      });
      sessionFound = true;
    }
  } catch {
    // No valid session (no cookie, expired, revoked) — handled below.
  }

  if (!sessionFound) {
    // Explicitly clear any stale "isAuthenticated: true" left over from a
    // previous session's persisted state — otherwise a guard would trust
    // that stale flag instead of what the server just told us.
    useAuthStore.getState().setUser(null);
    useAuthStore.getState().setInitialized(true);
  }
}

void bootstrapAuth();

// ============================================================================
// DEV: kill any previously-registered service worker (once)
// A dev SW precaches and serves stale JS bundles, so code changes don't reach
// the browser. devOptions.enabled is now false, but an SW registered by an
// earlier session lingers until unregistered. Do it once, then reload clean.
// ============================================================================

if (import.meta.env.DEV && typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  const DEV_SW_KEY = '__dev_sw_purged__';
  if (!sessionStorage.getItem(DEV_SW_KEY)) {
    navigator.serviceWorker.getRegistrations().then(async (regs) => {
      if (regs.length === 0) return;
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
      }
      sessionStorage.setItem(DEV_SW_KEY, '1');
      window.location.reload();
    });
  }
}

// ============================================================================
// PWA INITIALIZATION
// ============================================================================

initPWA();

// ============================================================================
// Runtime recovery for stale chunk/cache mismatches
// ============================================================================

if (typeof window !== 'undefined') {
  const RECOVERY_KEY = '__rq_chunk_recovery_done__';
  const isStaleChunkMessage = (message: string) => {
    const m = (message || '').toLowerCase();
    return (
      m.includes('query.isfetched is not a function') ||
      m.includes('failed to fetch dynamically imported module') ||
      m.includes('error loading dynamically imported module') ||
      m.includes('importing a module script failed')
    );
  };

  // Hard recovery for stale-deploy chunk 404s (e.g. "Visit Schedule won't open").
  // A new build changes asset hashes; a stale service-worker precache (old
  // index.html) then points at chunk/CSS files the server no longer has → 404.
  // Updating the SW isn't enough — we UNREGISTER all SWs + CLEAR all caches so
  // the reload fetches a fresh index.html (nginx serves it no-cache) referencing
  // the current hashes. Guarded to once per session to avoid reload loops.
  let recovering = false;
  const recoverFromStaleChunks = async () => {
    if (recovering || sessionStorage.getItem(RECOVERY_KEY) === '1') return;
    recovering = true;
    sessionStorage.setItem(RECOVERY_KEY, '1');

    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
      }
    } catch {
      // Best-effort cleanup; reload regardless.
    }

    window.location.reload();
  };

  // A failed React.lazy import surfaces differently across browsers/builds:
  // Vite dispatches `vite:preloadError`; the promise rejection fires
  // `unhandledrejection`; some engines emit a plain `error` event.
  window.addEventListener('vite:preloadError', () => {
    void recoverFromStaleChunks();
  });
  window.addEventListener('error', (event: ErrorEvent) => {
    if (isStaleChunkMessage(String(event.error?.message || event.message || ''))) {
      void recoverFromStaleChunks();
    }
  });
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason as { message?: string } | string | undefined;
    const message = typeof reason === 'string' ? reason : reason?.message || '';
    if (isStaleChunkMessage(message)) {
      void recoverFromStaleChunks();
    }
  });
}

// ============================================================================
// QUERY CLIENT CONFIGURATION
// ============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// ============================================================================
// APP SHELL (Tailwind-only, no MUI)
// ============================================================================

function ThemedApp() {
  const themeMode = useAppConfigStore((state) => state.themeMode);
  // School (tenant) branding for the browser tab title — falls back to the
  // generic "EduSphere" product name until the branding request resolves
  // (or if it fails), so the tab is never left blank.
  const { schoolName, logoUrl } = useSchoolBranding();

  React.useEffect(() => {
    const root = document.documentElement;
    if (themeMode === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    root.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  React.useEffect(() => {
    document.title = schoolName;
  }, [schoolName]);

  // Swap the tab favicon to the school's logo once branding resolves. The
  // static icons declared in index.html remain as the pre-hydration default
  // (and the fallback for tenants with no logo uploaded yet).
  React.useEffect(() => {
    if (!logoUrl) return;
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon'][data-branding]");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.setAttribute('data-branding', 'true');
      document.head.appendChild(link);
    }
    link.href = logoUrl;
  }, [logoUrl]);

  return (
    // Global TooltipProvider so any <Tooltip> across the app works without each
    // page mounting its own provider (Radix throws "Tooltip must be used within
    // TooltipProvider" otherwise).
    <TooltipProvider delayDuration={200}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
      {/* react-hot-toast Toaster — the sole toast system app-wide (sonner + the
          @/components/ui/toast wrapper were retired; every page now imports
          `toast` from 'react-hot-toast' directly). */}
      <HotToaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { fontSize: '0.875rem' },
          success: { iconTheme: { primary: 'hsl(var(--success, 142 71% 45%))', secondary: '#fff' } },
        }}
      />
    </TooltipProvider>
  );
}

// ============================================================================
// ROOT RENDER
// ============================================================================

// NOTE: React.StrictMode is intentionally NOT used here. Its dev-only
// double mount/unmount tears down react-draggable's event listeners inside
// react-grid-layout (the dashboard widget grid), which silently breaks
// drag/resize on the dev server. StrictMode is a no-op in production, so
// removing it changes nothing about shipped behaviour.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <ThemedApp />
    {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
  </QueryClientProvider>
);
